import React from "react";
import { Platform } from "react-native";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components/native";
import styled, { css } from "styled-components/native";
import { Button } from "@components/atoms";
import { ButtonType } from "@components/atoms/Button/types";
import Loader from "@components/atoms/Loader";
import { SpaceType } from "@components/atoms/Spacer/types";
import { PriceModes } from "@components/molecules/PriceModeCard/types";
import { ProductCategory } from "@utils/constants";
import { getButtonCategory } from "@utils/product/productValidators";
import LineViewContainer from "@scenes/requests/utils/LineView";
import PDPBottomTotalPriceSection from "../utils/PDPBottomTotalPriceSection";
import { FooterSpaceProps } from "../types";

interface PdpFooterProps {
  // Data
  itemDetail: any;
  productType: ProductCategory;
  priceMode: number;
  isCustomApp: boolean;
  isModal: boolean;
  isGuest: boolean;
  fromComponent?: boolean;

  // State
  itemQuantity: number;
  setItemQuantity: (quantity: number) => void;
  bottomButtons: any;
  isAPILoading: boolean;
  addToCartLoading: boolean;
  isShopifyCartLoading: boolean;

  // Layout
  screenHeight: number;
  height: number;
  maxContentLength: number;
  insets: any;

  // Handlers
  BottomButtonView: React.ComponentType<{ bottomButton: any }>;
  handleBottomButtonActions: (action: string) => void;
  handleOpen: () => void;
  handleOpenAskSupplier: () => void;

  // User info
  userInfo: any;

  // Settings
  enableBmhRetailMode: boolean;
}

const PdpFooter = ({
  itemDetail,
  productType,
  priceMode,
  isCustomApp,
  isModal,
  isGuest,
  fromComponent,
  itemQuantity,
  setItemQuantity,
  bottomButtons,
  isAPILoading,
  addToCartLoading,
  isShopifyCartLoading,
  screenHeight,
  height,
  maxContentLength,
  insets,
  BottomButtonView,
  handleBottomButtonActions,
  handleOpen,
  handleOpenAskSupplier,
  userInfo,
  enableBmhRetailMode,
}: PdpFooterProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <>
      {(!isCustomApp
        ? priceMode === PriceModes.WHITE ||
          (priceMode === PriceModes.ORANGE && enableBmhRetailMode)
        : (bottomButtons && bottomButtons.length) || isGuest) &&
        !isModal && (
          <>
            <LineViewContainer background="core-03" marginLeft={false} />
            <ButtonSectionView insets={insets}>
              <ButtonSectionContainer
                maxWidth={
                  theme.isTablet && screenHeight < 775
                    ? 900
                    : theme.isLargeScreen && height > 720
                    ? maxContentLength
                    : 1008
                }
              >
                <ButtonSectionChildContainer>
                  <ButtonsEndContainer>
                    {theme.isMobile &&
                    itemDetail?.custom_design_configuration &&
                    !(isModal && fromComponent) ? (
                      <PDPBottomTotalPriceSection
                        itemDetail={itemDetail}
                        itemQuantity={itemQuantity}
                        setQuantity={setItemQuantity}
                      />
                    ) : null}
                    {bottomButtons && bottomButtons.length ? (
                      bottomButtons.map(
                        (bottomButtonSection: any, i: number) => {
                          return (
                            <ButtonContainer
                              key={i}
                              length={bottomButtonSection.length}
                              description={
                                bottomButtonSection?.length
                                  ? bottomButtonSection[0]?.description
                                  : "bottom_right"
                              }
                            >
                              {bottomButtonSection && bottomButtonSection.length
                                ? bottomButtonSection.map(
                                    (bottomButton: any, j: number) => {
                                      switch (bottomButton.name) {
                                        case "total_price":
                                          return !(isModal && fromComponent) ? (
                                            <PDPBottomTotalPriceSection
                                              key={j}
                                              itemDetail={itemDetail}
                                              itemQuantity={itemQuantity}
                                              setQuantity={setItemQuantity}
                                            />
                                          ) : null;
                                        default:
                                          return (
                                            <BottomButtonView
                                              bottomButton={bottomButton}
                                              key={j}
                                            />
                                          );
                                      }
                                    }
                                  )
                                : null}
                            </ButtonContainer>
                          );
                        }
                      )
                    ) : (
                      <>
                        {!userInfo.isIFrame() && (
                          <ButtonFlexContainer minWidth={144}>
                            <Button
                              type={ButtonType.secondary}
                              textTypographyStyle={"H3-M"}
                              category={getButtonCategory(productType)}
                              title={t(
                                "productDetailPage.askSupplier.button.text"
                              )}
                              onPress={handleOpenAskSupplier}
                            />
                          </ButtonFlexContainer>
                        )}
                        {priceMode === PriceModes.WHITE && (
                          <ButtonFlexContainer minWidth={144}>
                            <Button
                              type={ButtonType.primary}
                              category={getButtonCategory(productType)}
                              title={t(
                                "productDetailPage.requestItem.button.text"
                              )}
                              onPress={() => handleBottomButtonActions("memo")}
                            />
                          </ButtonFlexContainer>
                        )}
                      </>
                    )}
                  </ButtonsEndContainer>
                </ButtonSectionChildContainer>
              </ButtonSectionContainer>
            </ButtonSectionView>
          </>
        )}
      {!isCustomApp &&
        userInfo.isMemoInOrangeModeOrPAUser(priceMode) &&
        !isModal &&
        itemDetail?.vendor_accepts_memo && (
          <>
            <LineViewContainer background="core-03" marginLeft={false} />
            <ButtonSectionView insets={insets}>
              <ButtonSectionContainer
                maxWidth={
                  theme.isTablet && screenHeight < 775
                    ? 900
                    : theme.isLargeScreen && height > 720
                    ? maxContentLength
                    : 1008
                }
              >
                <ButtonSectionChildContainer>
                  <ButtonsEndContainer>
                    <ButtonContainer description="bottom_left" />
                    <ButtonContainer description="bottom_right">
                      <ButtonFlexContainer>
                        <Button
                          type={ButtonType.primary}
                          category={getButtonCategory(productType)}
                          title={t("productDetailPage.request.button.text")}
                          onPress={() => handleOpen()}
                        />
                      </ButtonFlexContainer>
                    </ButtonContainer>
                  </ButtonsEndContainer>
                </ButtonSectionChildContainer>
              </ButtonSectionContainer>
            </ButtonSectionView>
          </>
        )}
      {(isAPILoading || addToCartLoading || isShopifyCartLoading) && (
        <Loader backgroundColor={theme["core-02"]} opacity={0.8} />
      )}
      {!theme.isMobile && <FooterSpace insets={insets} />}
    </>
  );
};

const FooterSpace = styled.View<FooterSpaceProps>`
  padding: ${({ insets }) =>
    insets.bottom > 0 ? 10 : Platform.OS === "android" ? 40 : 0}px;
  height: ${({ insets }) =>
    Platform.OS === "ios" ? (insets.bottom > 0 ? 10 : 0) : 10}px;
  background-color: ${({ theme }) => theme["core-01"]};
`;

const ButtonContainer = styled.View<{ description: string }>`
  flex-direction: row;
  ${({ theme, description }) =>
    !theme.isMobile
      ? css`
          width: 50%;
          gap: ${SpaceType.Extra}px;
          justify-content: ${({}) =>
            description === "bottom_left" ? "flex-start" : "flex-end"};
          align-items: center;
        `
      : css`
          width: 100%;
          gap: ${SpaceType.Basic}px;
          align-items: center;
          justify-content: space-between;
          padding: 0 ${SpaceType.Double}px;
        `}
`;

const ButtonsEndContainer = styled.View`
  ${({ theme }) =>
    !theme.isMobile &&
    css`
      justify-content: space-between;
      flex-direction: row;
      align-items: center;
      width: 100%;
    `}
`;

const ButtonFlexContainer = styled.View<any>`
  display: ${({ theme }) => (!theme.isMobile ? "block" : "flex")};
  max-width: ${({ theme }) => (theme.isTablet ? "180px" : "100%")};
  ${({ theme }) =>
    !theme.isMobile &&
    css`
      min-width: ${({ minWidth }) => (minWidth ? minWidth : "144px")};
    `};
  padding: ${SpaceType.Double}px 0;
  ${({ theme }) =>
    theme.isMobile &&
    css`
      flex: 1;
    `}
`;

const ButtonSectionView = styled.View<any>`
  background-color: ${({ theme }) => theme["core-01"]};
  padding-bottom: ${({ theme, insets }) =>
    !theme.isMobile
      ? "0px"
      : css`
          ${Platform.OS === "android"
            ? insets.bottom > 0
              ? insets.bottom
              : SpaceType.Double
            : insets.bottom}px
        `};
  align-items: center;
  justify-content: center;
`;

const ButtonSectionContainer = styled.View<any>`
  ${({ theme }) =>
    !theme.isMobile &&
    css`
      align-items: flex-end;
      justify-content: flex-end;
    `}
  width: ${({ theme }) => (theme.isLargeScreen ? "80%" : "100%")};
  max-width: ${({ maxWidth, theme }) => (theme.isMobile ? "100%" : maxWidth)};
`;

const ButtonSectionChildContainer = styled.View<{
  maxWidth: number;
}>`
  width: 100%;
`;

export default PdpFooter;
